/**
 * BrightPath API
 * Organized API methods for all resources
 */

import apiClient, { tokenManager, PaginatedResponse, ApiError } from './api-client';

// =============================================================================
// Re-exports
// =============================================================================

export { apiClient, tokenManager, type PaginatedResponse, type ApiError };
export { parseApiError, isNetworkError, isTimeoutError } from './api-client';

// =============================================================================
// API Methods
// =============================================================================

export const api = {
  // ===========================================================================
  // Authentication
  // ===========================================================================
  auth: {
    login: (data: { email: string; password: string }) =>
      apiClient.post('/auth/login', data),

    register: (data: {
      email: string;
      password: string;
      first_name: string;
      last_name: string;
    }) => apiClient.post('/auth/register', data),

    logout: () => apiClient.post('/auth/logout'),

    me: () => apiClient.get('/users/me'),

    refresh: () => apiClient.post('/auth/refresh'),

    forgotPassword: (email: string) =>
      apiClient.post('/auth/forgot-password', { email }),

    resetPassword: (token: string, password: string) =>
      apiClient.post('/auth/reset-password', { token, password }),

    verifyEmail: (token: string) =>
      apiClient.post('/auth/verify-email', { token }),

    resendVerification: (email: string) =>
      apiClient.post('/auth/resend-verification', { email }),
  },

  // ===========================================================================
  // Users
  // ===========================================================================
  users: {
    get: (id: string) => apiClient.get(`/users/${id}`),

    update: (id: string, data: Partial<{
      first_name: string;
      last_name: string;
      phone: string;
      avatar_url: string;
      timezone: string;
      notification_preferences: Record<string, boolean>;
    }>) => apiClient.patch(`/users/${id}`, data),

    updatePassword: (data: {
      current_password: string;
      new_password: string;
    }) => apiClient.post('/users/me/password', data),

    uploadAvatar: (formData: FormData) =>
      apiClient.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
  },

  // ===========================================================================
  // Families
  // ===========================================================================
  families: {
    list: () => apiClient.get('/families'),

    get: (id: string) => apiClient.get(`/families/${id}`),

    create: (data: {
      name: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    }) => apiClient.post('/families', data),

    update: (id: string, data: Partial<{
      name: string;
      address_line1: string;
      address_line2: string;
      city: string;
      state: string;
      postal_code: string;
      settings: Record<string, unknown>;
    }>) => apiClient.patch(`/families/${id}`, data),

    getChildren: (familyId: string) =>
      apiClient.get(`/families/${familyId}/children`),

    getMembers: (familyId: string) =>
      apiClient.get(`/families/${familyId}/members`),

    addMember: (familyId: string, data: {
      email: string;
      relationship: string;
      permissions?: Record<string, boolean>;
    }) => apiClient.post(`/families/${familyId}/members`, data),

    removeMember: (familyId: string, userId: string) =>
      apiClient.delete(`/families/${familyId}/members/${userId}`),
  },

  // ===========================================================================
  // Children
  // ===========================================================================
  children: {
    get: (id: string) => apiClient.get(`/children/${id}`),

    create: (familyId: string, data: {
      first_name: string;
      last_name: string;
      date_of_birth: string;
      gender?: string;
      nickname?: string;
    }) => apiClient.post(`/families/${familyId}/children`, data),

    update: (id: string, data: Partial<{
      first_name: string;
      last_name: string;
      nickname: string;
      date_of_birth: string;
      gender: string;
      blood_type: string;
      allergies: string[];
      current_medications: unknown[];
      insurance_provider: string;
      insurance_policy_number: string;
      medicaid_id: string;
      emergency_info: Record<string, unknown>;
      notes: string;
    }>) => apiClient.patch(`/children/${id}`, data),

    delete: (id: string) => apiClient.delete(`/children/${id}`),

    uploadAvatar: (id: string, formData: FormData) =>
      apiClient.post(`/children/${id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),

    getDiagnoses: (id: string) => apiClient.get(`/children/${id}/diagnoses`),

    addDiagnosis: (childId: string, data: {
      diagnosis_id: string;
      diagnosed_date?: string;
      diagnosed_by?: string;
      severity?: string;
      subtype?: string;
      notes?: string;
      is_primary?: boolean;
    }) => apiClient.post(`/children/${childId}/diagnoses`, data),

    removeDiagnosis: (childId: string, diagnosisId: string) =>
      apiClient.delete(`/children/${childId}/diagnoses/${diagnosisId}`),
  },

  // ===========================================================================
  // Health Logs
  // ===========================================================================
  healthLogs: {
    list: (childId: string, params?: {
      page?: number;
      page_size?: number;
      log_type_id?: string;
      start_date?: string;
      end_date?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    }) => apiClient.get(`/children/${childId}/health-logs`, { params }),

    get: (id: string) => apiClient.get(`/health-logs/${id}`),

    create: (childId: string, data: {
      log_type_id: string;
      occurred_at: string;
      data: Record<string, unknown>;
      severity?: number;
      duration_minutes?: number;
      notes?: string;
      location?: string;
    }) => apiClient.post(`/children/${childId}/health-logs`, data),

    update: (id: string, data: Partial<{
      occurred_at: string;
      data: Record<string, unknown>;
      severity: number;
      duration_minutes: number;
      notes: string;
      location: string;
    }>) => apiClient.patch(`/health-logs/${id}`, data),

    delete: (id: string) => apiClient.delete(`/health-logs/${id}`),

    getStats: (childId: string, params?: {
      start_date?: string;
      end_date?: string;
      log_type_id?: string;
    }) => apiClient.get(`/children/${childId}/health-logs/stats`, { params }),

    getTypes: () => apiClient.get('/health-log-types'),

    exportCsv: (childId: string, params?: {
      start_date?: string;
      end_date?: string;
      log_type_ids?: string[];
    }) => apiClient.get(`/children/${childId}/health-logs/export`, {
      params,
      responseType: 'blob',
    }),
  },

  // ===========================================================================
  // Medications
  // ===========================================================================
  medications: {
    list: (childId: string) => apiClient.get(`/children/${childId}/medications`),

    get: (id: string) => apiClient.get(`/medications/${id}`),

    create: (childId: string, data: {
      name: string;
      dosage: string;
      frequency: string;
      route?: string;
      prescribing_doctor?: string;
      start_date?: string;
      instructions?: string;
    }) => apiClient.post(`/children/${childId}/medications`, data),

    update: (id: string, data: Partial<{
      name: string;
      dosage: string;
      frequency: string;
      route: string;
      end_date: string;
      instructions: string;
      is_active: boolean;
    }>) => apiClient.patch(`/medications/${id}`, data),

    delete: (id: string) => apiClient.delete(`/medications/${id}`),
  },

  // ===========================================================================
  // Documents
  // ===========================================================================
  documents: {
    list: (familyId: string, params?: {
      page?: number;
      page_size?: number;
      child_id?: string;
      folder_id?: string;
      document_type?: string;
      search?: string;
    }) => apiClient.get(`/families/${familyId}/documents`, { params }),

    get: (id: string) => apiClient.get(`/documents/${id}`),

    upload: (familyId: string, formData: FormData) =>
      apiClient.post(`/families/${familyId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),

    update: (id: string, data: Partial<{
      title: string;
      description: string;
      document_type: string;
      document_date: string;
      provider_name: string;
      tags: string[];
      folder_id: string;
    }>) => apiClient.patch(`/documents/${id}`, data),

    delete: (id: string) => apiClient.delete(`/documents/${id}`),

    getDownloadUrl: (id: string) => apiClient.get(`/documents/${id}/download-url`),

    shareWithAttorney: (id: string, share: boolean) =>
      apiClient.patch(`/documents/${id}/share`, { is_shared_with_attorney: share }),

    // Folders
    getFolders: (familyId: string) =>
      apiClient.get(`/families/${familyId}/document-folders`),

    createFolder: (familyId: string, data: {
      name: string;
      parent_id?: string;
      color?: string;
    }) => apiClient.post(`/families/${familyId}/document-folders`, data),

    deleteFolder: (folderId: string) =>
      apiClient.delete(`/document-folders/${folderId}`),
  },

  // ===========================================================================
  // Expenses
  // ===========================================================================
  expenses: {
    list: (familyId: string, params?: {
      page?: number;
      page_size?: number;
      child_id?: string;
      category?: string;
      start_date?: string;
      end_date?: string;
    }) => apiClient.get(`/families/${familyId}/expenses`, { params }),

    get: (id: string) => apiClient.get(`/expenses/${id}`),

    create: (familyId: string, data: {
      category: string;
      description: string;
      amount: number;
      expense_date: string;
      child_id?: string;
      subcategory?: string;
      vendor_name?: string;
      payment_method?: string;
      is_insurance_covered?: boolean;
      insurance_amount?: number;
      is_tax_deductible?: boolean;
      notes?: string;
    }) => apiClient.post(`/families/${familyId}/expenses`, data),

    update: (id: string, data: Partial<{
      category: string;
      subcategory: string;
      description: string;
      amount: number;
      expense_date: string;
      vendor_name: string;
      is_insurance_covered: boolean;
      insurance_amount: number;
      out_of_pocket: number;
      is_tax_deductible: boolean;
      notes: string;
    }>) => apiClient.patch(`/expenses/${id}`, data),

    delete: (id: string) => apiClient.delete(`/expenses/${id}`),

    getReport: (familyId: string, params: {
      start_date: string;
      end_date: string;
      group_by?: 'category' | 'month' | 'child';
    }) => apiClient.get(`/families/${familyId}/expenses/report`, { params }),

    exportCsv: (familyId: string, params?: {
      start_date?: string;
      end_date?: string;
    }) => apiClient.get(`/families/${familyId}/expenses/export`, {
      params,
      responseType: 'blob',
    }),

    attachReceipt: (expenseId: string, formData: FormData) =>
      apiClient.post(`/expenses/${expenseId}/receipt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
  },

  // ===========================================================================
  // Appointments
  // ===========================================================================
  appointments: {
    list: (familyId: string, params?: {
      page?: number;
      page_size?: number;
      child_id?: string;
      start_date?: string;
      end_date?: string;
      status?: string;
    }) => apiClient.get(`/families/${familyId}/appointments`, { params }),

    get: (id: string) => apiClient.get(`/appointments/${id}`),

    create: (familyId: string, data: {
      title: string;
      starts_at: string;
      ends_at: string;
      child_id?: string;
      appointment_type?: string;
      provider_name?: string;
      location_type?: 'in_person' | 'telehealth';
      location_name?: string;
      location_address?: string;
      telehealth_link?: string;
      notes?: string;
      reminder_minutes?: number[];
    }) => apiClient.post(`/families/${familyId}/appointments`, data),

    update: (id: string, data: Partial<{
      title: string;
      starts_at: string;
      ends_at: string;
      status: string;
      provider_name: string;
      location_type: string;
      location_name: string;
      location_address: string;
      telehealth_link: string;
      notes: string;
    }>) => apiClient.patch(`/appointments/${id}`, data),

    delete: (id: string) => apiClient.delete(`/appointments/${id}`),

    cancel: (id: string, reason?: string) =>
      apiClient.post(`/appointments/${id}/cancel`, { reason }),
  },

  // ===========================================================================
  // Care Team
  // ===========================================================================
  careTeam: {
    list: (familyId: string) => apiClient.get(`/families/${familyId}/care-team`),

    get: (id: string) => apiClient.get(`/care-team/${id}`),

    create: (familyId: string, data: {
      name: string;
      member_type_id: string;
      child_id?: string;
      email?: string;
      phone?: string;
      organization?: string;
      specialty?: string;
      notes?: string;
      is_primary?: boolean;
    }) => apiClient.post(`/families/${familyId}/care-team`, data),

    update: (id: string, data: Partial<{
      name: string;
      email: string;
      phone: string;
      fax: string;
      organization: string;
      address: string;
      specialty: string;
      npi_number: string;
      notes: string;
      is_primary: boolean;
      is_active: boolean;
    }>) => apiClient.patch(`/care-team/${id}`, data),

    delete: (id: string) => apiClient.delete(`/care-team/${id}`),

    getTypes: () => apiClient.get('/care-team-member-types'),
  },

  // ===========================================================================
  // Goals
  // ===========================================================================
  goals: {
    list: (childId: string, params?: {
      status?: string;
      goal_type?: string;
      is_iep_goal?: boolean;
    }) => apiClient.get(`/children/${childId}/goals`, { params }),

    get: (id: string) => apiClient.get(`/goals/${id}`),

    create: (childId: string, data: {
      title: string;
      description?: string;
      goal_type?: string;
      target_date?: string;
      measurement_type?: string;
      baseline_value?: string;
      target_value?: string;
      is_iep_goal?: boolean;
      iep_area?: string;
      care_team_member_id?: string;
    }) => apiClient.post(`/children/${childId}/goals`, data),

    update: (id: string, data: Partial<{
      title: string;
      description: string;
      status: string;
      progress_percentage: number;
      target_date: string;
      current_value: string;
    }>) => apiClient.patch(`/goals/${id}`, data),

    delete: (id: string) => apiClient.delete(`/goals/${id}`),

    addProgress: (goalId: string, data: {
      entry_date: string;
      value?: string;
      notes?: string;
    }) => apiClient.post(`/goals/${goalId}/progress`, data),

    getProgress: (goalId: string) => apiClient.get(`/goals/${goalId}/progress`),
  },

  // ===========================================================================
  // Resources
  // ===========================================================================
  resources: {
    list: (params?: {
      page?: number;
      page_size?: number;
      category_id?: string;
      diagnosis_id?: string;
      resource_type?: string;
      search?: string;
      tags?: string[];
    }) => apiClient.get('/resources', { params }),

    get: (slug: string) => apiClient.get(`/resources/${slug}`),

    getByDiagnosis: (diagnosisSlug: string, params?: {
      page?: number;
      page_size?: number;
      category_id?: string;
    }) => apiClient.get(`/diagnoses/${diagnosisSlug}/resources`, { params }),

    getCategories: () => apiClient.get('/resource-categories'),

    save: (resourceId: string, notes?: string) =>
      apiClient.post(`/resources/${resourceId}/save`, { notes }),

    unsave: (resourceId: string) =>
      apiClient.delete(`/resources/${resourceId}/save`),

    getSaved: (params?: { page?: number; page_size?: number }) =>
      apiClient.get('/resources/saved', { params }),

    rate: (resourceId: string, rating: number) =>
      apiClient.post(`/resources/${resourceId}/rate`, { rating }),
  },

  // ===========================================================================
  // Diagnoses
  // ===========================================================================
  diagnoses: {
    list: () => apiClient.get('/diagnoses'),

    get: (slug: string) => apiClient.get(`/diagnoses/${slug}`),
  },

  // ===========================================================================
  // Marketplace
  // ===========================================================================
  marketplace: {
    getCategories: () => apiClient.get('/marketplace/categories'),

    getVendors: (params?: {
      page?: number;
      page_size?: number;
      category_id?: string;
      state?: string;
      search?: string;
      is_verified?: boolean;
    }) => apiClient.get('/marketplace/vendors', { params }),

    getVendor: (slug: string) => apiClient.get(`/marketplace/vendors/${slug}`),

    getProducts: (params?: {
      page?: number;
      page_size?: number;
      vendor_id?: string;
      category_id?: string;
      diagnosis_id?: string;
      min_price?: number;
      max_price?: number;
      search?: string;
    }) => apiClient.get('/marketplace/products', { params }),

    getProduct: (id: string) => apiClient.get(`/marketplace/products/${id}`),

    getProductReviews: (productId: string, params?: {
      page?: number;
      page_size?: number;
    }) => apiClient.get(`/marketplace/products/${productId}/reviews`, { params }),

    createReview: (productId: string, data: {
      rating: number;
      title?: string;
      content?: string;
    }) => apiClient.post(`/marketplace/products/${productId}/reviews`, data),

    createInquiry: (vendorId: string, data: {
      product_id?: string;
      subject: string;
      message: string;
    }) => apiClient.post(`/marketplace/vendors/${vendorId}/inquiries`, data),
  },

  // ===========================================================================
  // Community
  // ===========================================================================
  community: {
    // Forums
    getForumCategories: () => apiClient.get('/community/forums/categories'),

    getPosts: (categorySlug: string, params?: {
      page?: number;
      page_size?: number;
      sort_by?: 'recent' | 'popular' | 'unanswered';
    }) => apiClient.get(`/community/forums/${categorySlug}/posts`, { params }),

    getPost: (id: string) => apiClient.get(`/community/posts/${id}`),

    createPost: (categorySlug: string, data: {
      title: string;
      content: string;
      post_type?: 'discussion' | 'question' | 'announcement';
    }) => apiClient.post(`/community/forums/${categorySlug}/posts`, data),

    updatePost: (id: string, data: Partial<{
      title: string;
      content: string;
    }>) => apiClient.patch(`/community/posts/${id}`, data),

    deletePost: (id: string) => apiClient.delete(`/community/posts/${id}`),

    likePost: (id: string) => apiClient.post(`/community/posts/${id}/like`),

    unlikePost: (id: string) => apiClient.delete(`/community/posts/${id}/like`),

    // Replies
    getReplies: (postId: string, params?: {
      page?: number;
      page_size?: number;
    }) => apiClient.get(`/community/posts/${postId}/replies`, { params }),

    createReply: (postId: string, data: {
      content: string;
      parent_reply_id?: string;
    }) => apiClient.post(`/community/posts/${postId}/replies`, data),

    markAsSolution: (replyId: string) =>
      apiClient.post(`/community/replies/${replyId}/solution`),

    // Mentors
    getMentors: (params?: {
      page?: number;
      page_size?: number;
      diagnosis_id?: string;
      expertise_area?: string;
    }) => apiClient.get('/community/mentors', { params }),

    getMentor: (id: string) => apiClient.get(`/community/mentors/${id}`),

    requestMentor: (mentorId: string, data: {
      message: string;
    }) => apiClient.post(`/community/mentors/${mentorId}/request`, data),

    // Support Groups
    getSupportGroups: (params?: {
      page?: number;
      page_size?: number;
      diagnosis_id?: string;
      group_type?: 'virtual' | 'local';
      state?: string;
    }) => apiClient.get('/community/support-groups', { params }),

    joinSupportGroup: (groupId: string) =>
      apiClient.post(`/community/support-groups/${groupId}/join`),

    leaveSupportGroup: (groupId: string) =>
      apiClient.delete(`/community/support-groups/${groupId}/membership`),
  },

  // ===========================================================================
  // Notifications
  // ===========================================================================
  notifications: {
    list: (params?: {
      page?: number;
      page_size?: number;
      is_read?: boolean;
      category?: string;
    }) => apiClient.get('/notifications', { params }),

    markRead: (id: string) => apiClient.patch(`/notifications/${id}/read`),

    markAllRead: () => apiClient.patch('/notifications/read-all'),

    getUnreadCount: () => apiClient.get('/notifications/unread-count'),

    updatePreferences: (preferences: Record<string, boolean>) =>
      apiClient.patch('/notifications/preferences', { preferences }),
  },

  // ===========================================================================
  // Telehealth
  // ===========================================================================
  telehealth: {
    createSession: (appointmentId: string) =>
      apiClient.post(`/telehealth/sessions`, { appointment_id: appointmentId }),

    getSession: (id: string) => apiClient.get(`/telehealth/sessions/${id}`),

    getAccessToken: (sessionId: string) =>
      apiClient.get(`/telehealth/sessions/${sessionId}/token`),

    endSession: (id: string) =>
      apiClient.post(`/telehealth/sessions/${id}/end`),
  },
};

export default api;
